import React, { useState, useCallback } from 'react';
import { formatFileSize } from '../utils/helpers';
import './FileUpload.css';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

function FileUpload({ onUpload }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [currentFileName, setCurrentFileName] = useState('');

  const handleFiles = useCallback(async (files) => {
    if (files.length === 0) return;

    setError('');

    // Upload files sequentially
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setError(`${file.name} ist zu groß. Maximum: ${formatFileSize(MAX_FILE_SIZE)}`);
        continue;
      }

      setUploading(true);
      setUploadProgress(0);
      setCurrentFileName(`${file.name} (${i + 1}/${files.length})`);

      try {
        await onUpload(file, (progress) => {
          setUploadProgress(progress);
        });

        // Success - short delay before next file
        setUploadProgress(100);
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (err) {
        console.error('Upload error:', err);
        setError(`Fehler beim Hochladen von ${file.name}`);
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    }

    // Reset after all uploads
    setUploading(false);
    setUploadProgress(0);
    setCurrentFileName('');
  }, [onUpload]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, [handleFiles]);

  const handleFileInput = useCallback((e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
    e.target.value = ''; // Reset input
  }, [handleFiles]);

  return (
    <div className="file-upload">
      <div
        className={`drop-zone ${isDragging ? 'dragging' : ''} ${uploading ? 'uploading' : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="drop-content">
          {uploading ? (
            <div className="upload-progress">
              <div className="progress-info">
                <span className="file-name">{currentFileName}</span>
                <span className="progress-percent">{uploadProgress}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          ) : (
            <>
              <div className="drop-icon">📁</div>
              <p className="drop-text">
                Dateien hierher ziehen oder
              </p>
              <label className="file-input-label">
                <input
                  type="file"
                  className="file-input"
                  onChange={handleFileInput}
                  disabled={uploading}
                  multiple
                />
                <span className="btn btn-primary">
                  Dateien auswählen
                </span>
              </label>
              <p className="drop-hint">
                Maximum: {formatFileSize(MAX_FILE_SIZE)}
              </p>
            </>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
    </div>
  );
}

export default FileUpload;
